import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Sparkles, Award, Clock, ShoppingCart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem('serviceCart') || '[]');
    setCartCount(cart.length);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" data-testid="navbar-logo" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <img src="/cleo-logo.png" alt="Cleo Professionals" style={{ height: '100px', width: 'auto' }} />
        </Link>
        
        <ul className="navbar-menu">
          <li><Link to="/" className="navbar-link" data-testid="nav-home">Home</Link></li>
          <li><Link to="/services" className="navbar-link" data-testid="nav-services">Services</Link></li>
          <li><Link to="/gallery" className="navbar-link" data-testid="nav-gallery">Gallery</Link></li>
          <li><Link to="/booking" className="navbar-cta blink-animation" data-testid="nav-booking">Book Now</Link></li>
        </ul>
        
        {/* Cart with Text - Before Hamburger */}
        <Link 
          to="/booking" 
          className="cart-with-label"
          style={{ 
            position: 'relative',
            marginLeft: '2rem',
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'linear-gradient(135deg, #d4a574, #c89b64)',
            borderRadius: '8px',
            textDecoration: 'none',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 165, 116, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ 
            color: 'white', 
            fontWeight: '600',
            fontSize: '1rem',
            letterSpacing: '0.5px'
          }} className="blink-animation">
            Cart
          </span>
          <div style={{ position: 'relative' }}>
            <ShoppingCart size={20} color="white" />
            {cartCount > 0 && (
              <span className="cart-badge-red" style={{
                background: '#ff4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: '700',
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                border: '2px solid white'
              }}>
                {cartCount}
              </span>
            )}
          </div>
        </Link>
        
        <button 
          className={`mobile-menu-btn ${mobileMenuOpen ? 'hidden' : ''}`}
          data-testid="mobile-menu-btn" 
          onClick={toggleMobileMenu}
        >
          ☰
        </button>
      </div>
      
      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'mobile-menu-open' : ''}`} onClick={closeMobileMenu}>
        <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-menu-header">
            <img src="/cleo-logo.png" alt="Cleo Professionals" style={{ height: '60px', width: 'auto' }} />
            <button className="mobile-menu-close" onClick={closeMobileMenu}>✕</button>
          </div>
          <ul className="mobile-menu-list">
            <li><Link to="/" className="mobile-menu-link" onClick={closeMobileMenu}>HOME</Link></li>
            <li><Link to="/services" className="mobile-menu-link" onClick={closeMobileMenu}>Services</Link></li>
            <li><Link to="/gallery" className="mobile-menu-link" onClick={closeMobileMenu}>Gallery</Link></li>
            <li>
              <Link to="/booking" className="mobile-menu-link" onClick={closeMobileMenu} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                <ShoppingCart size={18} className="blink-animation" />
                Cart
                {cartCount > 0 && (
                  <span className="cart-badge-red" style={{
                    background: '#ff4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    marginLeft: '0.25rem'
                  }}>
                    {cartCount}
                  </span>
                )}
              </Link>
            </li>
            <li><Link to="/booking" className="mobile-menu-link mobile-menu-cta blink-animation" onClick={closeMobileMenu}>Book Now</Link></li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

const Footer = () => {
  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-section">
          <img src="/cleo-logo.png" alt="Cleo Professionals" style={{ height: '120px', width: 'auto', marginBottom: '1rem' }} />
          <h3>Cleo Professionals</h3>
          <p>
            Experience luxury beauty treatments in a serene environment. 
            We are committed to making you look and feel your absolute best.
          </p>
        </div>
        <div className="footer-section">
          <h3>Quick Links</h3>
          <ul className="footer-links">
            <li><Link to="/services">Services</Link></li>
            <li><Link to="/gallery">Gallery</Link></li>
            <li><Link to="/booking">Book Appointment</Link></li>
            <li><Link to="/admin">Admin Login</Link></li>
          </ul>
        </div>
        <div className="footer-section">
          <h3>Contact</h3>
          <p>📍 Location: Aditya Heritage, 307, Rustomjee Elanza Ave, Malad, Ram Nagar, Malad West, Mumbai, Maharashtra 400064</p>
          <p>📞 Phone: <a href="tel:+912245066363" style={{ color: 'inherit', textDecoration: 'none' }}>+91 22-45066363</a></p>
          <p>📱 Mobile: <a href="tel:+918097236363" style={{ color: 'inherit', textDecoration: 'none' }}>+91 8097236363</a></p>
          <p>🕒 Hours: Mon - Sun 10:00 AM - 8:00 PM</p>
          <p>📍 <a href="https://maps.app.goo.gl/DntkvEhWTEzgoxuh6" target="_blank" rel="noopener noreferrer" style={{ color: '#d4a574', textDecoration: 'none' }}>View on Google Maps</a></p>
        </div>
        <div className="footer-section">
          <h3>Follow Us</h3>
          <p>Stay connected for updates and beauty tips!</p>
          <div style={{ marginTop: '1rem' }}>
            <a 
              href="https://www.instagram.com/cleoprofessionals/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#d4a574', 
                textDecoration: 'none',
                fontSize: '1.125rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📷 Instagram
            </a>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; 2025 Cleo Professionals Beauty Salon (Maxine Corporation). All rights reserved.</p>
      </div>
    </footer>
  );
};

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-decorative-circle" style={{ top: '10%', right: '10%' }}></div>
        <div className="hero-container">
          <div className="hero-content">
            <span className="hero-badge" data-testid="hero-badge">✨ Premium Beauty Services</span>
            <h1 className="hero-title" data-testid="hero-title">
              Experience Luxury Beauty at Cleo Professionals
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              Transform your look with our expert stylists and premium treatments. 
              From hair care to nail art, we offer comprehensive beauty solutions tailored just for you.
            </p>
            <div className="hero-buttons">
              <Link to="/booking" className="btn-primary" data-testid="hero-book-btn">
                <Calendar size={20} />
                Book Appointment
              </Link>
              <Link to="/services" className="btn-secondary" data-testid="hero-services-btn">
                View Services
              </Link>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600&h=800&fit=crop" 
              alt="Beauty salon" 
              className="hero-image-main"
              data-testid="hero-image"
            />
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="services-section" data-testid="services-preview-section">
        <div className="services-container">
          <div className="section-header">
            <span className="section-badge">Our Services</span>
            <h2 className="section-title">Comprehensive Beauty Solutions</h2>
            <p className="section-subtitle">
              Explore our wide range of professional services designed to enhance your natural beauty
            </p>
          </div>
          <div className="services-grid">
            <div className="service-card" data-testid="service-preview-hair">
              <div className="service-icon">💇</div>
              <h3>Hair Services</h3>
              <p>Expert hair cuts, coloring, treatments, and styling for all hair types. Transform your look with our skilled stylists.</p>
              <Link to="/services" className="service-link">Explore Hair Services →</Link>
            </div>
            <div className="service-card" data-testid="service-preview-facial">
              <div className="service-icon">✨</div>
              <h3>Facial & Skin Care</h3>
              <p>Rejuvenating facials, cleanups, and skin treatments to give you radiant, glowing skin.</p>
              <Link to="/services" className="service-link">Explore Facial Services →</Link>
            </div>
            <div className="service-card" data-testid="service-preview-spa">
              <div className="service-icon">🧖</div>
              <h3>Body Spa & Massage</h3>
              <p>Relax and unwind with our luxurious spa treatments and therapeutic massages.</p>
              <Link to="/services" className="service-link">Explore Spa Services →</Link>
            </div>
            <div className="service-card" data-testid="service-preview-nails">
              <div className="service-icon">💅</div>
              <h3>Nail Care</h3>
              <p>Professional manicures, pedicures, nail art, and extensions for beautiful nails.</p>
              <Link to="/services" className="service-link">Explore Nail Services →</Link>
            </div>
            <div className="service-card" data-testid="service-preview-waxing">
              <div className="service-icon">🪒</div>
              <h3>Waxing & Threading</h3>
              <p>Smooth and flawless skin with our gentle waxing and precise threading services.</p>
              <Link to="/services" className="service-link">Explore Waxing Services →</Link>
            </div>
            <div className="service-card" data-testid="service-preview-bridal">
              <div className="service-icon">👰</div>
              <h3>Special Treatments</h3>
              <p>Premium treatments including Botox, Keratin, Balayage, and more for special occasions.</p>
              <Link to="/services" className="service-link">Explore Special Treatments →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="services-section" style={{ background: '#fafafa' }}>
        <div className="services-container">
          <div className="section-header">
            <span className="section-badge">Why Choose Us</span>
            <h2 className="section-title">Excellence in Every Detail</h2>
          </div>
          <div className="services-grid">
            <div className="service-card" data-testid="feature-experts">
              <div className="service-icon"><Award size={32} color="#d4a574" /></div>
              <h3>Expert Professionals</h3>
              <p>Our team consists of highly trained and certified beauty experts with years of experience.</p>
            </div>
            <div className="service-card" data-testid="feature-products">
              <div className="service-icon"><Sparkles size={32} color="#d4a574" /></div>
              <h3>Premium Products</h3>
              <p>We use only the finest quality products and latest techniques for best results.</p>
            </div>
            <div className="service-card" data-testid="feature-hygiene">
              <div className="service-icon"><Clock size={32} color="#d4a574" /></div>
              <h3>Flexible Timings</h3>
              <p>Open 7 days a week from 9 AM to 8 PM for your convenience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section" data-testid="testimonials-section">
        <div className="testimonials-container">
          <div className="section-header">
            <span className="section-badge">Testimonials</span>
            <h2 className="section-title">What Our Clients Say</h2>
            <p className="section-subtitle">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </div>
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="testimonials-grid">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="testimonial-card" data-testid={`testimonial-${testimonial.id}`}>
                  <div className="testimonial-header">
                    <img 
                      src={testimonial.image_url || 'https://via.placeholder.com/60'} 
                      alt={testimonial.customer_name} 
                      className="testimonial-avatar"
                    />
                    <div className="testimonial-info">
                      <h4>{testimonial.customer_name}</h4>
                      <div className="testimonial-rating">
                        {'⭐'.repeat(testimonial.rating)}
                      </div>
                    </div>
                  </div>
                  <p className="testimonial-text">"{testimonial.comment}"</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-section" style={{ background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)', color: 'white' }}>
        <div className="services-container" style={{ textAlign: 'center' }}>
          <h2 className="section-title" style={{ color: 'white', marginBottom: '1.5rem' }}>
            Ready to Transform Your Look?
          </h2>
          <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '2.5rem' }}>
            Book your appointment today and experience the luxury you deserve
          </p>
          <Link to="/booking" className="btn-primary" data-testid="cta-book-btn" style={{ margin: '0 auto' }}>
            <Calendar size={20} />
            Book Appointment Now
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
